import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import FormatBar from '../format_bar/formatBar';
import './adminUddannelseEvents.css';

const AdminUddannelseEvents = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Uddannelse ID fra URL'en
  const descriptionRef = useRef(null);

  // Auth state
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Data states
  const [uddannelse, setUddannelse] = useState(null);
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // UI states
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Format states
  const [descriptionFormats, setDescriptionFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  // Check auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setIsLoading(false);
      if (!user) {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load uddannelse og events
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Hent uddannelsen
        const uddannelserDocRef = doc(db, 'content', 'uddannelser');
        const uddannelserDoc = await getDoc(uddannelserDocRef);
        
        if (uddannelserDoc.exists() && uddannelserDoc.data().uddannelser) {
          const uddannelser = uddannelserDoc.data().uddannelser;
          const currentUddannelse = uddannelser.find(u => u.id === id);
          
          if (currentUddannelse) {
            setUddannelse(currentUddannelse);
            // Få events fra uddannelsen hvis de findes
            setEvents(currentUddannelse.events || []);
          } else {
            setMessage({
              type: 'error',
              text: 'Uddannelsen blev ikke fundet'
            });
            setTimeout(() => navigate('/admin/uddannelse'), 3000);
          }
        } else {
          setMessage({
            type: 'error',
            text: 'Uddannelsesdata blev ikke fundet'
          });
        }
      } catch (error) {
        console.error('Fejl ved hentning af uddannelsesdata:', error);
        setMessage({
          text: 'Der opstod en fejl ved indlæsning af indhold',
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user, id, navigate]);

  // Initialisering af editoren når editingEvent ændrer sig
  useEffect(() => {
    if (editingEvent && descriptionRef.current) {
      descriptionRef.current.innerHTML = editingEvent.description || '';
    }
  }, [editingEvent]);

  // Tilføj event listener for selection change
  useEffect(() => {
    const handleSelectionChange = () => {
      if (!editingEvent) return;
      
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      
      const range = selection.getRangeAt(0);
      if (!descriptionRef.current) return;
      
      // Tjek om selektionen er inden for editoren
      if (descriptionRef.current.contains(range.commonAncestorContainer)) {
        checkActiveFormats();
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [editingEvent]);

  const checkActiveFormats = () => {
    setDescriptionFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      heading: false,
      bullet: document.queryCommandState('insertUnorderedList'),
      link: document.queryCommandState('createLink'),
      underline: document.queryCommandState('underline')
    });
  };

  // Håndter link-indsættelse
  const handleLinkInsertion = (editor) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();
    
    // Tjek om selektionen er inden for editoren
    if (!editor || !editor.contains(range.commonAncestorContainer)) {
      return;
    }
    
    // Tjek om den valgte tekst er en URL
    const url = isValidUrl(selectedText) ? selectedText : prompt('Indtast URL:', 'https://');
    
    if (url && url.trim() !== '') {
      const linkText = selectedText || prompt('Indtast link tekst:', '');
      
      if (linkText && linkText.trim() !== '') {
        // Indsæt link direkte i HTML
        const linkElement = document.createElement('a');
        linkElement.href = url;
        linkElement.target = '_blank';
        linkElement.rel = 'noopener noreferrer';
        linkElement.textContent = linkText;
        
        // Indsæt linket
        range.deleteContents();
        range.insertNode(linkElement);
        
        // Flyt cursoren efter linket
        range.setStartAfter(linkElement);
        range.setEndAfter(linkElement);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleFormatClick = (formatType) => {
    if (!descriptionRef.current) return;
    
    // Fokuser på editoren hvis den ikke allerede har fokus
    if (!descriptionRef.current.contains(document.activeElement)) {
      descriptionRef.current.focus();
    }

    // Udfør formateringen
    switch (formatType) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      
      case 'italic':
        document.execCommand('italic', false);
        break;
      
      case 'heading':
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        if (!range) return;
        
        const parentElement = range.commonAncestorContainer.parentElement;
        const isHeading = parentElement && parentElement.tagName === 'H2';
        
        if (isHeading) {
          document.execCommand('formatBlock', false, 'p');
        } else {
          document.execCommand('formatBlock', false, 'h2');
        }
        break;
      
      case 'bullet':
        document.execCommand('insertUnorderedList', false);
        break;
      
      case 'link':
        handleLinkInsertion(descriptionRef.current);
        break;
        
      case 'underline':
        document.execCommand('underline', false);
        break;
      
      default:
        break;
    }

    // Opdater aktive formateringer
    checkActiveFormats();
  };

  const isValidUrl = (text) => {
    try {
      if (text.match(/^(https?:\/\/|www\.)/i)) {
        const url = text.startsWith('www.') ? `https://${text}` : text;
        new URL(url);
        return url;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak', false);
    }
  };

  // Handlere for events
  const handleEditEvent = (event) => {
    setEditingEvent({...event});
    setPreview(event.imageUrl);
    setIsCreating(false);
  };

  const handleCreateEvent = () => {
    setEditingEvent({
      id: '',
      title: '',
      description: '',
      imageUrl: '',
      externalLink: '',
      buttonText: 'Tilmeld'
    });
    setPreview(null);
    setSelectedFile(null);
    setIsCreating(true);
  };

  const handleEventInputChange = (e) => {
    const { name, value } = e.target;
    setEditingEvent(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Er du sikker på, at du vil slette dette event?')) {
      try {
        // Find event der skal slettes
        const event = events.find(e => e.id === eventId);
        
        // Slet billedet hvis det er gemt i storage
        if (event.imageUrl && event.imageUrl.includes('firebase')) {
          const imagePath = event.imageUrl.split('/').pop();
          const imageRef = ref(storage, `uddannelse-events/${imagePath}`);
          try {
            await deleteObject(imageRef);
          } catch (error) {
            console.warn('Billedet kunne ikke slettes fra storage:', error);
          }
        }
        
        // Opdater array uden det slettede event
        const updatedEvents = events.filter(e => e.id !== eventId);
        setEvents(updatedEvents);
        
        // Opdater uddannelse med de nye events
        const uddannelserDocRef = doc(db, 'content', 'uddannelser');
        const uddannelserDoc = await getDoc(uddannelserDocRef);
        
        if (uddannelserDoc.exists()) {
          const uddannelser = uddannelserDoc.data().uddannelser;
          const updatedUddannelser = uddannelser.map(u => {
            if (u.id === id) {
              return { ...u, events: updatedEvents };
            }
            return u;
          });
          
          await setDoc(doc(db, 'content', 'uddannelser'), {
            uddannelser: updatedUddannelser,
            lastUpdated: new Date()
          });
          
          setMessage({ type: 'success', text: 'Event blev slettet!' });
        }
      } catch (error) {
        console.error("Fejl ved sletning:", error);
        setMessage({ type: 'error', text: 'Fejl ved sletning af event.' });
      }
    }
  };

  const handleSaveEvent = async () => {
    setIsSaving(true);
    try {
      // Hent det aktuelle indhold fra editoren direkte før gem
      let eventDescription = '';
      if (descriptionRef.current) {
        eventDescription = descriptionRef.current.innerHTML;
      }
      
      if (!editingEvent.id && isCreating) {
        // Generer et ID baseret på titlen hvis det er et nyt event
        editingEvent.id = editingEvent.title
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '') + '-' + Date.now();
      }
      
      let imageUrl = editingEvent.imageUrl;
      
      // Upload nyt billede hvis det er valgt
      if (selectedFile) {
        const storageRef = ref(storage, `uddannelse-events/${editingEvent.id}-${Date.now()}`);
        await uploadBytes(storageRef, selectedFile);
        imageUrl = await getDownloadURL(storageRef);
      }
      
      const updatedEvent = {
        ...editingEvent,
        description: eventDescription,
        imageUrl
      };
      
      let updatedEvents;
      
      if (isCreating) {
        updatedEvents = [...events, updatedEvent];
      } else {
        updatedEvents = events.map(e => 
          e.id === updatedEvent.id ? updatedEvent : e
        );
      }
      
      // Opdater uddannelse med de nye events
      const uddannelserDocRef = doc(db, 'content', 'uddannelser');
      const uddannelserDoc = await getDoc(uddannelserDocRef);
      
      if (uddannelserDoc.exists()) {
        const uddannelser = uddannelserDoc.data().uddannelser;
        const updatedUddannelser = uddannelser.map(u => {
          if (u.id === id) {
            return { ...u, events: updatedEvents };
          }
          return u;
        });
        
        await setDoc(doc(db, 'content', 'uddannelser'), {
          uddannelser: updatedUddannelser,
          lastUpdated: new Date()
        });
        
        setEvents(updatedEvents);
        setEditingEvent(null);
        setSelectedFile(null);
        setPreview(null);
        setIsCreating(false);
        
        setMessage({ 
          type: 'success', 
          text: isCreating ? 'Nyt event oprettet!' : 'Event opdateret!' 
        });
      }
    } catch (error) {
      console.error("Fejl ved gem:", error);
      setMessage({ 
        type: 'error', 
        text: 'Fejl ved gem af event.' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingEvent(null);
    setPreview(null);
    setSelectedFile(null);
    setIsCreating(false);
  };

  const closeMessage = () => {
    setMessage({ type: '', text: '' });
  };

  const goBack = () => {
    navigate('/admin/uddannelse');
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Indlæser...</p>
      </div>
    );
  }

  if (!uddannelse) {
    return (
      <div className="admin-loading">
        <p>Uddannelsen kunne ikke findes...</p>
        <button onClick={goBack} className="back-button">
          ← Tilbage til uddannelser
        </button>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-logo">
          <img 
            src="/media/logo/psykologiskperspektiv-logo.avif" 
            alt="Psykologisk Perspektiv" 
            onClick={() => navigate('/admin')}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <div className="admin-header-right">
          <div className="admin-user">
            <span>Logget ind som: </span>
            <strong>{user.email}</strong>
          </div>
          <button onClick={() => navigate('/admin')} className="back-button">
            ← Tilbage
          </button>
        </div>
      </header>
      
      <main className="admin-content">
        <div className="admin-uddannelse-events-container">
          <button className="back-button" onClick={goBack}>
            ← Tilbage til uddannelser
          </button>
          
          <h1>Administrer events for: {uddannelse.title}</h1>
          
          {message.text && (
            <div className={`admin-message ${message.type}`}>
              <p>{message.text}</p>
              <button 
                className="close-message" 
                onClick={closeMessage}
              >
                ×
              </button>
            </div>
          )}
          
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Events</h2>
              {!editingEvent && (
                <button 
                  className="create-btn"
                  onClick={handleCreateEvent}
                >
                  + Opret nyt event
                </button>
              )}
            </div>
            
            <p className="admin-description">
              Her kan du administrere events for uddannelsen "{uddannelse.title}".
            </p>
            
            {editingEvent ? (
              <div className="event-form">
                <div className="form-group">
                  <label htmlFor="title">Titel</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={editingEvent.title}
                    onChange={handleEventInputChange}
                    placeholder="Eventtitel"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Beskrivelse</label>
                  <FormatBar 
                    activeFormats={descriptionFormats}
                    onFormatClick={handleFormatClick}
                  />
                  <div
                    ref={descriptionRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    data-placeholder="Skriv eventets beskrivelse her..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="buttonText">Knaptekst</label>
                  <input
                    type="text"
                    id="buttonText"
                    name="buttonText"
                    value={editingEvent.buttonText}
                    onChange={handleEventInputChange}
                    placeholder="F.eks. 'Tilmeld' eller 'Læs mere'"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="externalLink">Eksternt link</label>
                  <input
                    type="text"
                    id="externalLink"
                    name="externalLink"
                    value={editingEvent.externalLink}
                    onChange={handleEventInputChange}
                    placeholder="Link til tilmelding eller mere information"
                  />
                </div>

                <div className="image-uploader">
                  <label>Billede</label>
                  {preview && (
                    <div className="image-preview">
                      <img src={preview} alt="Forhåndsvisning" />
                    </div>
                  )}
                  <label className="upload-btn">
                    {preview ? 'Skift billede' : 'Vælg billede'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileChange}
                    />
                  </label>
                </div>

                <div className="form-actions">
                  <button 
                    className="save-btn"
                    onClick={handleSaveEvent}
                    disabled={isSaving || !editingEvent.title}
                  >
                    {isSaving ? 'Gemmer...' : isCreating ? 'Opret event' : 'Gem ændringer'}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            ) : (
              <>
                {events.length > 0 ? (
                  <div className="uddannelse-event-list">
                    {events.map((event) => (
                      <div key={event.id} className="event-item">
                        <div className="event-preview">
                          <div className="event-image">
                            <img src={event.imageUrl || '/media/placeholder.png'} alt={event.title} />
                          </div>
                          <div className="event-content">
                            <h3>{event.title}</h3>
                            <p>{event.description}</p>
                            {event.externalLink && (
                              <a className="event-link" href={event.externalLink} target="_blank" rel="noopener noreferrer">
                                {event.buttonText || 'Tilmeld'}
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="event-actions">
                          <button 
                            className="edit-btn"
                            onClick={() => handleEditEvent(event)}
                          >
                            Rediger
                          </button>
                          <button 
                            className="delete-btn"
                            onClick={() => handleDeleteEvent(event.id)}
                          >
                            Slet
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-items">
                    <p>Ingen events fundet. Opret et nyt event for at komme i gang.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      
      <footer className="admin-footer">
        <p>&copy; {new Date().getFullYear()} Psykologisk Perspektiv - Admin Panel</p>
      </footer>
    </div>
  );
};

export default AdminUddannelseEvents;
